import {
  createFileRoute,
  Outlet,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { AdminLayout } from "../components/Layouts/AdminLayout";
import { NotFoundComponent } from "@/components/NotFoundComponent";
import { Toaster } from "@/components/ui/sonner";
import {
  supabase,
  setStoredSessionTimeoutMinutes,
} from "@/ajax/clients/supabase";
import { useEffect } from "react";
import { queryClient } from "@/ajax/queryClient";
import OfflineHandler from "@/components/OfflineHandler";
import { TRPCClientError } from "@trpc/client";
import { trpc } from "@/trpc";
import posthog from "posthog-js";
import * as Sentry from "@sentry/browser";
import { enforceMfaGate } from "@/features/mfa/enforceMfaGate";
import { isImpersonating } from "@/features/impersonation/impersonation-session";

export const Route = createFileRoute("/_admin")({
  loader: async ({ location }) => {
    const [userResponse] = await Promise.allSettled([
      queryClient.fetchQuery(trpc.user.getUser.queryOptions()),
    ]);

    if (userResponse.status === "rejected") {
      if (userResponse.reason instanceof TRPCClientError) {
        if (userResponse.reason.data?.code === "UNAUTHORIZED") {
          throw redirect({
            to: "/auth-org/signin",
            reloadDocument: true,
          });
        }
      }
    }

    let user = null;
    if (userResponse.status === "fulfilled") {
      user = userResponse.value;
    }

    // 2FA gate: if this user must use 2FA and the session's second factor
    // isn't verified yet, redirect to MFA setup. The API also enforces this
    // independently.
    if (user) {
      await enforceMfaGate(user.mfa_required, location.pathname);
    }

    // While impersonating, analytics and error reports stay tied to the
    // staff member's own sign-in.
    if (!isImpersonating()) {
      Sentry.setUser({
        id: user?.id,
      });
      posthog.identify(user?.id);
    }

    return {
      user,
    };
  },
  notFoundComponent: () => <NotFoundComponent homePath="/admin/dashboard" />,
  component: AdminRouteComponent,
});

function AdminRouteComponent() {
  const { user } = Route.useLoaderData();
  const router = useRouter();

  useEffect(() => {
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        queryClient.clear();
        router.navigate({
          to: "/auth-org/signin",
          reloadDocument: true,
        });
      }
    });
  }, [router]);

  useEffect(() => {
    if (user && !isImpersonating()) {
      void setStoredSessionTimeoutMinutes(user.session_timeout_minutes ?? null);
    }
  }, [user]);

  if (!user) {
    return <OfflineHandler homePath="/admin/dashboard" />;
  }
  return (
    <AdminLayout user={user}>
      <Outlet />
      <Toaster />
    </AdminLayout>
  );
}
